"use client";

import React, { useEffect, useState } from "react";
import { Head } from "./Head";
import { GoogleTagManager } from "@next/third-parties/google";
import { usePathname } from "next/navigation";
import { getCookieValue, setCookieValue } from "@/utils/cookies";
import { hasCookie } from "cookies-next";
import DatadogInit from "@/services/Datadog/datadog-init";
import AlertConsoleLog from "@/utils/AlertConsoleLog";
import Utms from "@/utils/UTM";
import { getUrlBodyClass } from "@/utils";
import CookiePolicy from "@/components/atoms/CookiePolicy/cookiePolicy";
import { AmplitudeContextProvider } from "@/contexts/Amplitude";
import { GlobalProvider } from "@/contexts/SiteDataContext";
import { LayoutWrapperProps } from "./types";
import Disclaimer from "@/components/organisms/Disclaimer";
import { useGetSiteFont } from "@/hooks/useGetSiteFont";
import { getSiteHasExperiments, shouldPersistSiteData } from "./helpers";
const { disclaimer } = require("../config/default");
import ChatWidget from "@/services/ChatWidget";
import TrustpilotScript from "@/services/Trustpilot";
import { isTrustpilotDomain } from "@/components/molecules/Trustpilot/constants";
import { SiteDataFromApiProps } from "./types";
import "../styles/_base.scss";

export const LayoutWrapper = ({
  children,
  language,
  siteDataFromApi,
  configData,
  siteDataComplete,
  t,
  host,
  bgColor,
  plainHeader,
  headerTitle,
  headerLanding,
  headerCompact,
  hideFooterNavigation,
  headerFull,
  backgroundColor,
  hideFooterNavigationLogo,
  hideHeader,
  footerTemplate,
  footerDisclaimerType,
  className,
  headerBigMaxWidth,
  footerBigMaxWidth,
  disclaimerBigMaxWidth,
  collapsedMobileTest,
  hideLanguageSelector,
}: LayoutWrapperProps) => {
  const [hideDisclaimer, setHideDisclaimer] = useState(false);
  const [mounted, setMounted] = useState(false);

  const stickyName = `u-sticky-footer ${bgColor}`;
  const backgroundColorMode = `main--${backgroundColor}`;
  let orderCookie = getCookieValue("address_token");
  const siteHasGtm = typeof siteDataFromApi === "object" && siteDataFromApi && siteDataFromApi.gtm;
  const siteFont = useGetSiteFont(configData.web_fontFamily);
  const siteTheme =
    typeof siteDataFromApi === "object" && siteDataFromApi !== null && "theme" in siteDataFromApi
      ? siteDataFromApi.theme
      : "classic";
  const siteHasExperiments = getSiteHasExperiments(siteDataFromApi);
  const trustpilotDomain =
    typeof siteDataFromApi === "object" && siteDataFromApi !== null
      ? (siteDataFromApi as SiteDataFromApiProps).domain
      : undefined;
  const hasTrustpilot = isTrustpilotDomain(trustpilotDomain);

  const pathname = usePathname();
  const bodyClass = getUrlBodyClass(pathname);

  useEffect(() => {
    if (!hasCookie("siteData") && shouldPersistSiteData(siteDataFromApi)) {
      setCookieValue("siteData", siteDataFromApi);
    }
  }, [siteDataFromApi, configData]);

  useEffect(() => {
    const path = window.location.pathname;
    setMounted(true), setHideDisclaimer(disclaimer.notShowIn.includes(path));
  }, [pathname]);

  return (
    <html
      lang={language}
      data-scroll-behavior="smooth">
      <Head
        siteTheme={siteTheme}
        siteHasExperiments={siteHasExperiments}
        host={host}
      />
      <body className={`${siteFont} ${bodyClass}`}>
        <GlobalProvider
          siteDatafromProps={siteDataFromApi}
          configDataFromProps={configData}
          siteDataCompleteFromProps={siteDataComplete}
          t={t}
          language={language}
          sourceURL="">
          <DatadogInit />
          <ChatWidget />
          {hasTrustpilot && <TrustpilotScript />}
          <Utms />
          {siteHasGtm && <GoogleTagManager gtmId={siteDataFromApi.gtm} />}
          <AlertConsoleLog />
          <AmplitudeContextProvider>
            <div className="content-wrapper">
              {mounted && !hideDisclaimer && (
                <Disclaimer
                  bigMaxWidth={disclaimerBigMaxWidth}
                  className="disclaimer--desktop"
                />
              )}
              {children}
              <CookiePolicy t={t} />
            </div>
          </AmplitudeContextProvider>
        </GlobalProvider>
      </body>
    </html>
  );
};

export default LayoutWrapper;
